import {
    AdminPanelSettingsOutlined,
    EditOutlined,
    ForwardToInboxOutlined,
    LockResetOutlined,
    PeopleOutlined,
} from '@mui/icons-material';
import {
    Box,
    Chip,
    FormControl,
    IconButton,
    Link,
    MenuItem,
    Paper,
    Select,
    Skeleton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

import Result from 'components/v2/common/Result';
import { useAuth } from 'contexts/ProtectedRoute';

import type { AdminUsers } from 'api/models/response/AdminUsers';
import { ACTIONS } from 'constant';
import { canEditUserProfile, canEditUserRole, getAssignableRoles } from 'data/constants/roles';

export type AdminUserAction = ACTIONS | 'RESET_LINK' | 'PERMISSIONS' | 'CHANGE_ROLE';

interface AdminUsersTableProps {
    users: AdminUsers[];
    loading?: boolean;
    updatingRoleUserId?: number | null;
    onAction: (action: AdminUserAction, user: AdminUsers, extra?: string) => void;
}

const formatRole = (role?: string) =>
    role
        ? role
              .toLowerCase()
              .split('_')
              .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
              .join(' ')
        : '—';

const getFullName = (user: AdminUsers) =>
    `${user.firstname || ''} ${user.lastname || ''}`.trim() || user.email || `User #${user.id}`;

const formatCompany = (name?: string) =>
    (name ? name.split('[').join('').split(']').join('') : '') || '—';

function StatusChip({ status }: { status?: string }) {
    const isInvited = (status || '').toUpperCase() === 'INVITED';
    return (
        <Chip
            size="small"
            label={isInvited ? 'Invited' : 'Confirmed'}
            sx={{
                height: 22,
                fontSize: '0.75rem',
                fontWeight: 600,
                borderRadius: 1,
                bgcolor: isInvited ? 'rgba(245, 158, 11, 0.12)' : 'rgba(34, 197, 94, 0.12)',
                color: isInvited ? '#b45309' : '#15803d',
                '& .MuiChip-label': { px: 1 },
            }}
        />
    );
}

const actionBtnSx = { p: 0.5 };

function RowActions({
    user,
    onAction,
}: {
    user: AdminUsers;
    onAction: (action: AdminUserAction, user: AdminUsers) => void;
}) {
    const { role } = useAuth();
    const canEdit = canEditUserProfile(role, user.role);
    const isInvited = (user.status || '').toUpperCase() === 'INVITED';

    return (
        <Stack direction="row" spacing={0.25} sx={{ justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            {isInvited ? (
                <Tooltip title="Resend invitation">
                    <IconButton
                        size="small"
                        color="primary"
                        sx={actionBtnSx}
                        onClick={() => onAction(ACTIONS.RESEND, user)}
                    >
                        <ForwardToInboxOutlined sx={{ fontSize: 18 }} />
                    </IconButton>
                </Tooltip>
            ) : null}

            {canEdit ? (
                <Tooltip title="Edit profile">
                    <IconButton
                        size="small"
                        sx={actionBtnSx}
                        onClick={() => onAction(ACTIONS.EDIT, user)}
                    >
                        <EditOutlined sx={{ fontSize: 18 }} />
                    </IconButton>
                </Tooltip>
            ) : null}

            <Tooltip title="Send password reset link">
                <IconButton
                    size="small"
                    sx={actionBtnSx}
                    onClick={() => onAction('RESET_LINK', user)}
                >
                    <LockResetOutlined sx={{ fontSize: 18 }} />
                </IconButton>
            </Tooltip>

            <Tooltip title="Permissions">
                <IconButton
                    size="small"
                    sx={actionBtnSx}
                    onClick={() => onAction('PERMISSIONS', user)}
                >
                    <AdminPanelSettingsOutlined sx={{ fontSize: 18 }} />
                </IconButton>
            </Tooltip>
        </Stack>
    );
}

function RoleCell({
    user,
    updating,
    onChange,
}: {
    user: AdminUsers;
    updating: boolean;
    onChange: (role: string) => void;
}) {
    const { role } = useAuth();
    const canEdit = canEditUserRole(role, user.role);
    const assignableRoles = getAssignableRoles(role);
    const options = Array.from(new Set([...assignableRoles, ...(user.role ? [user.role] : [])]));

    if (!canEdit) {
        return (
            <Typography variant="body2" color="text.secondary">
                {formatRole(user.role)}
            </Typography>
        );
    }

    return (
        <FormControl
            size="small"
            fullWidth
            disabled={updating}
            sx={{ minWidth: 140, maxWidth: 200 }}
        >
            <Select
                value={user.role ?? ''}
                displayEmpty
                onChange={(e) => onChange(String(e.target.value))}
                sx={{ fontSize: 13, borderRadius: 1.5 }}
            >
                {options.map((roleValue) => (
                    <MenuItem key={roleValue} value={roleValue} sx={{ fontSize: 13 }}>
                        {formatRole(roleValue)}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
}

export function AdminUsersTable({
    users,
    loading = false,
    updatingRoleUserId = null,
    onAction,
}: AdminUsersTableProps) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const denseCellSx = {
        py: 0.75,
        px: 1.25,
    };

    if (loading) {
        if (isMobile) {
            return (
                <Stack spacing={1}>
                    {Array.from({ length: 4 }).map((_, i) => (
                        <Paper key={i} variant="outlined" sx={{ p: 1.5, borderRadius: 1.5 }}>
                            <Skeleton width="50%" />
                            <Skeleton width="70%" />
                            <Skeleton width="40%" />
                        </Paper>
                    ))}
                </Stack>
            );
        }
        return (
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1.5 }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            {['ID', 'Name', 'Email', 'Company', 'Role', 'Status', 'Actions'].map(
                                (h) => (
                                    <TableCell key={h} sx={denseCellSx}>
                                        {h}
                                    </TableCell>
                                )
                            )}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {Array.from({ length: 5 }).map((_, i) => (
                            <TableRow key={i}>
                                {Array.from({ length: 7 }).map((__, j) => (
                                    <TableCell key={j} sx={denseCellSx}>
                                        <Skeleton />
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        );
    }

    if (!users.length) {
        return (
            <Result
                icon={<PeopleOutlined sx={{ fontSize: 32 }} />}
                title="No users found"
                description="Try adjusting search or company filters."
            />
        );
    }

    if (isMobile) {
        return (
            <Stack spacing={1}>
                {users.map((user) => (
                    <Paper
                        key={user.id}
                        variant="outlined"
                        sx={{ p: 1.5, borderRadius: 1.5, minWidth: 0 }}
                    >
                        <Stack
                            direction="row"
                            sx={{ justifyContent: 'space-between', gap: 1, mb: 1 }}
                        >
                            <Box sx={{ minWidth: 0 }}>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontWeight: 600,
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                    }}
                                >
                                    {getFullName(user)}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ wordBreak: 'break-word', display: 'block' }}
                                >
                                    {user.email}
                                </Typography>
                            </Box>
                            <StatusChip status={user.status} />
                        </Stack>
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block' }}
                        >
                            ID: {user.id} · {formatCompany(user.company_name)}
                        </Typography>
                        <Box sx={{ mt: 1, mb: 1 }}>
                            <RoleCell
                                user={user}
                                updating={updatingRoleUserId === user.id}
                                onChange={(nextRole) => onAction('CHANGE_ROLE', user, nextRole)}
                            />
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <RowActions user={user} onAction={onAction} />
                        </Box>
                    </Paper>
                ))}
            </Stack>
        );
    }

    return (
        <TableContainer
            component={Paper}
            variant="outlined"
            sx={{ borderRadius: 1.5, overflowX: 'auto' }}
        >
            <Table
                size="small"
                sx={{
                    minWidth: 900,
                    '& .MuiTableCell-root': denseCellSx,
                }}
            >
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ fontWeight: 700, width: 64 }}>ID</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Email</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Company</TableCell>
                        <TableCell sx={{ fontWeight: 700, minWidth: 160 }}>Role</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, width: 140 }}>
                            Actions
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {users.map((user) => (
                        <TableRow key={user.id} hover>
                            <TableCell>
                                <Typography variant="body2" color="text.secondary">
                                    {user.id}
                                </Typography>
                            </TableCell>
                            <TableCell>
                                <Link
                                    component={RouterLink}
                                    to={`/dashboard/${user.id}/client-profile`}
                                    underline="hover"
                                    sx={{ fontWeight: 600, fontSize: 13 }}
                                >
                                    {getFullName(user)}
                                </Link>
                            </TableCell>
                            <TableCell>
                                <Typography
                                    variant="body2"
                                    sx={{ wordBreak: 'break-word', lineHeight: 1.3 }}
                                >
                                    {user.email}
                                </Typography>
                            </TableCell>
                            <TableCell>
                                <Typography variant="body2">
                                    {formatCompany(user.company_name)}
                                </Typography>
                            </TableCell>
                            <TableCell>
                                <RoleCell
                                    user={user}
                                    updating={updatingRoleUserId === user.id}
                                    onChange={(nextRole) => onAction('CHANGE_ROLE', user, nextRole)}
                                />
                            </TableCell>
                            <TableCell>
                                <StatusChip status={user.status} />
                            </TableCell>
                            <TableCell align="right">
                                <RowActions user={user} onAction={onAction} />
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

export default AdminUsersTable;
